import React, { useEffect, useState, useCallback } from "react";
import Swal from "sweetalert2";
import {
  fetchIntakePools,
  fetchIntakes,
  createIntake,
  createQCCheck,
  fetchQuarantineQC,
  approveQCCheck,
  rejectQCCheck,
} from "../../api/IntakeQCApi";
import "../../styles/IntakeQC.css";

// ─── QC ROW (stable outside component — never remounts) ───────────────────────
const QCRow = ({ label, status, setStatus, commentKey, commentValue, onCommentChange }) => (
  <div className="qc-row">
    <div className="qc-row-label">
      <span className="qc-dot" />
      <span>{label}</span>
    </div>
    <div className="qc-row-controls">
      <div className="toggle-group">
        <button type="button" className={`toggle-btn ${status === "PASS" ? "active-pass" : ""}`} onClick={() => setStatus("PASS")}>PASS</button>
        <button type="button" className={`toggle-btn ${status === "FAIL" ? "active-fail" : ""}`} onClick={() => setStatus("FAIL")}>FAIL</button>
      </div>
      <textarea value={commentValue} onChange={(e) => onCommentChange(commentKey, e.target.value)} placeholder="Required comment..." rows={2} />
    </div>
  </div>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const intakeFarmName     = (intake) => intake?.pool_details?.farm_name ?? "";
const quarantineFarmName = (qc)     => qc?.intake_details?.pool?.farm_name ?? "";
const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const fmtDate = (iso) => new Date(iso).toLocaleDateString();

/**
 * Derive the true remaining-in-quarantine balance from a QC record.
 *
 * The backend sends:
 *   qc_nets        – original nets placed into quarantine
 *   available_nets – qc_nets minus SUM(split_children.qc_nets)   ← source of truth
 *   quarantine_stems – stems proportional to qc_nets (original ratio)
 *
 * We recalculate stems proportionally so the stems figure matches the
 * current available_nets rather than the original qc_nets.
 */
const deriveQuarantineBalance = (qc) => {
  const originalNets    = qc.qc_nets        ?? 0;
  const availableNets   = qc.available_nets  ?? originalNets;
  const originalStems   = qc.quarantine_stems ?? 0;

  // Stem ratio from original figures; avoid division by zero
  const stemRatio = originalNets > 0 ? originalStems / originalNets : 0;
  const availableStems = Math.round(availableNets * stemRatio);

  // Nets already resolved (summed from split children)
  const resolvedNets  = originalNets - availableNets;
  const resolvedStems = Math.round(resolvedNets * stemRatio);

  return { originalNets, availableNets, availableStems, resolvedNets, resolvedStems, stemRatio };
};

/**
 * Walk split_history.children and tally resolved nets by status.
 * split_history can be an object (root node) or already the children array —
 * normalise both shapes.
 */
const tallySplitChildren = (splitHistory) => {
  if (!splitHistory) return { approved: 0, rejected: 0, children: [] };

  // The serialiser returns split_history as the ROOT node object
  const children = Array.isArray(splitHistory)
    ? splitHistory
    : (splitHistory.children ?? []);

  let approved = 0;
  let rejected = 0;
  for (const c of children) {
    if (c.status === "APPROVED") approved += c.qc_nets ?? 0;
    if (c.status === "REJECTED") rejected += c.qc_nets ?? 0;
  }
  return { approved, rejected, children };
};

// ─── Main component ───────────────────────────────────────────────────────────
const IntakeQC = () => {
  const [pools, setPools]               = useState([]);
  const [intakes, setIntakes]           = useState([]);
  const [quarantine, setQuarantine]     = useState([]);
  const [selectedFarm, setSelectedFarm] = useState("");
  const [currentView, setCurrentView]   = useState("selector");
  const [expandedQuarantine, setExpandedQuarantine] = useState(null);

  const [form, setForm] = useState({ pool: "", received_nets: "", received_stems: "" });

  const [qcForm, setQcForm] = useState({
    intake: "",
    qc_nets: "",
    status: "APPROVED",
    pest_disease_ok: null,   pest_disease_comment: "",
    cut_stage_ok: null,      cut_stage_comment: "",
    hooks_ok: null,          hooks_comment: "",
    arrangement_ok: null,    arrangement_comment: "",
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [poolData, intakeData, quarantineData] = await Promise.all([
        fetchIntakePools(),
        fetchIntakes(),
        fetchQuarantineQC(),
      ]);
      setPools(poolData || []);
      setIntakes(intakeData || []);
      // Only show quarantine records that still have nets to resolve
      setQuarantine((quarantineData || []).filter((q) => (q.available_nets ?? q.qc_nets) > 0));
    } catch {
      Swal.fire("Error", "Failed to load data", "error");
    }
  };

  // ── Derived lists ──────────────────────────────────────────────────────────
  const filteredPools = selectedFarm
    ? pools.filter((p) => p.farm_name === selectedFarm)
    : pools;

  const filteredIntakes = intakes.filter((i) => {
    const farmMatch    = !selectedFarm || intakeFarmName(i) === selectedFarm;
    const isToday      = new Date(i.received_at).toDateString() === new Date().toDateString();
    const hasRemaining = !i.qc_progress?.is_fully_qced;
    return farmMatch && isToday && hasRemaining;
  });

  const filteredQuarantine = quarantine.filter(
    (q) => !selectedFarm || quarantineFarmName(q) === selectedFarm
  );

  // ── Simple event handlers ──────────────────────────────────────────────────
  const farmList = ["RVR 1", "RVR 2"];

  const handleFarmSelect       = (farm) => { setSelectedFarm(farm); setCurrentView("dashboard"); };
  const toggleQuarantineExpand = (id)   => setExpandedQuarantine((prev) => (prev === id ? null : id));

  const handleBackToSelector = async () => {
    const r = await Swal.fire({
      title: "Change Farm?",
      text: `You are currently managing ${selectedFarm}. Switch to a different farm?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Change Farm",
      cancelButtonText:  "Stay Here",
      confirmButtonColor: "#a92f6f",
      cancelButtonColor:  "#9e9a93",
      customClass: { popup: "qc-swal-popup" },
    });
    if (r.isConfirmed) { setSelectedFarm(""); setCurrentView("selector"); setExpandedQuarantine(null); }
  };

  const handleNetChange = (value) => {
    const nets = parseInt(value || 0);
    setForm((prev) => ({ ...prev, received_nets: value, received_stems: nets * 50 }));
  };

  const handleIntakeSelect = (intakeId) => {
    const intake    = intakes.find((i) => String(i.id) === String(intakeId));
    const remaining = intake?.qc_progress?.remaining_qc ?? intake?.received_nets ?? "";
    setQcForm((prev) => ({ ...prev, intake: intakeId, qc_nets: intake ? String(remaining) : "" }));
  };

  const handleQcComment = useCallback((key, value) => {
    setQcForm((prev) => ({ ...prev, [`${key}_comment`]: value }));
  }, []);

  const getPoolDetails   = (poolId)   => pools.find((p)   => String(p.id) === String(poolId))   || {};
  const getIntakeDetails = (intakeId) => intakes.find((i)  => String(i.id) === String(intakeId)) || {};

  const CHECK_KEYS = [
    { key: "pest_disease", label: "Pest & Disease" },
    { key: "cut_stage",    label: "Cut Stage" },
    { key: "hooks",        label: "Hooks" },
    { key: "arrangement",  label: "Arrangement" },
  ];

  // ═══════════════════════════════════════════════════════════════════════════
  // CONFIRMATION POPUPS
  // ═══════════════════════════════════════════════════════════════════════════

  const showIntakeConfirmation = async () => {
    const pool = getPoolDetails(form.pool);
    const r = await Swal.fire({
      title: "Confirm Intake", width: 520,
      html: `
        <div class="confirm-popup">
          <div class="confirm-header">
            <div class="confirm-icon intake">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 4v16m-8-8h16"/></svg>
            </div>
            <p class="confirm-subtitle">Review your intake details before submitting</p>
          </div>
          <div class="confirm-details">
            <div class="confirm-row"><span class="confirm-label">Farm</span><span class="confirm-value">${pool.farm_name || "—"}</span></div>
            <div class="confirm-row"><span class="confirm-label">GHSE</span><span class="confirm-value">${pool.ghse_name || "—"}</span></div>
            <div class="confirm-row"><span class="confirm-label">Variety</span><span class="confirm-value">${pool.variety_name || "—"}</span></div>
            <div class="confirm-divider"></div>
            <div class="confirm-row highlight"><span class="confirm-label">Nets Received</span><span class="confirm-value big">${form.received_nets || 0}</span></div>
            <div class="confirm-row highlight"><span class="confirm-label">Stems Calculated</span><span class="confirm-value big olive">${form.received_stems || 0}</span></div>
          </div>
        </div>`,
      showCancelButton: true,
      confirmButtonText: "Submit Intake",
      cancelButtonText:  "Edit Details",
      confirmButtonColor: "#a92f6f", cancelButtonColor: "#9e9a93",
      customClass: { popup: "qc-swal-popup confirm-swal" },
    });
    return r.isConfirmed;
  };

  const showQCConfirmation = async () => {
    const intake = getIntakeDetails(qcForm.intake);
    const pool   = intake.pool_details || {};

    const checksHtml = CHECK_KEYS.map(({ key, label }) => {
      const ok      = qcForm[`${key}_ok`];
      const comment = qcForm[`${key}_comment`] || "";
      return `
        <div class="confirm-check-row">
          <div class="confirm-check-info">
            <span class="confirm-check-name">${label}</span>
            <span class="confirm-check-comment">${comment}</span>
          </div>
          <span class="confirm-check-badge ${ok ? "pass" : "fail"}">${ok ? "PASS" : "FAIL"}</span>
        </div>`;
    }).join("");

    const r = await Swal.fire({
      title: "Confirm QC Report", width: 560,
      html: `
        <div class="confirm-popup">
          <div class="confirm-header">
            <div class="confirm-icon qc">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            </div>
            <p class="confirm-subtitle">Review quality control report before submission</p>
          </div>
          <div class="confirm-details">
            <div class="confirm-row"><span class="confirm-label">Farm</span><span class="confirm-value">${pool.farm_name || "—"}</span></div>
            <div class="confirm-row"><span class="confirm-label">GHSE</span><span class="confirm-value">${pool.ghse_name || "—"}</span></div>
            <div class="confirm-row"><span class="confirm-label">Variety</span><span class="confirm-value">${pool.variety_name || "—"}</span></div>
            <div class="confirm-row highlight"><span class="confirm-label">QC Nets</span><span class="confirm-value big">${qcForm.qc_nets || 0}</span></div>
            <div class="confirm-row"><span class="confirm-label">Overall Status</span><span class="confirm-value status-${qcForm.status.toLowerCase()}">${qcForm.status}</span></div>
            <div class="confirm-divider"></div>
            <div class="confirm-checks-title">Inspection Results</div>
            ${checksHtml}
          </div>
        </div>`,
      showCancelButton: true,
      confirmButtonText: "Submit QC Report",
      cancelButtonText:  "Edit Report",
      confirmButtonColor: "#a92f6f", cancelButtonColor: "#9e9a93",
      customClass: { popup: "qc-swal-popup confirm-swal" },
    });
    return r.isConfirmed;
  };

  const showReviewConfirmation = async (qc, action, formData) => {
    const pool         = qc.intake_details?.pool || {};
    const isApprove    = action === "approve";
    const actionedNets = formData.partial_nets;
    // Use available_nets as the true "still in quarantine" figure
    const { availableNets, availableStems, stemRatio } = deriveQuarantineBalance(qc);
    const remainingAfter     = availableNets - actionedNets;
    const remainingStemsAfter = Math.round(remainingAfter * stemRatio);
    const isPartial          = remainingAfter > 0;

    const checksHtml = CHECK_KEYS.map(({ key, label }) => {
      const s = formData[`${key}_status`];
      const c = formData[`${key}_comment`] || "";
      return `
        <div class="confirm-check-row">
          <div class="confirm-check-info">
            <span class="confirm-check-name">${label}</span>
            <span class="confirm-check-comment">${c}</span>
          </div>
          <span class="confirm-check-badge ${s === "PASS" ? "pass" : "fail"}">${s}</span>
        </div>`;
    }).join("");

    const splitNote = isPartial ? `
      <div style="margin:12px 0;padding:10px 14px;background:#fff8e6;border-left:3px solid #e6a817;border-radius:6px;font-size:13px;color:#6b4c00;">
        <strong>${actionedNets} nets</strong> will be ${isApprove ? "approved" : "rejected"}.<br/>
        <strong>${remainingAfter} nets</strong> (≈ <strong>${remainingStemsAfter} stems</strong>) will remain in <strong>QUARANTINE</strong>.
      </div>` : "";

    const r = await Swal.fire({
      title: isApprove ? "Confirm Approval" : "Confirm Rejection", width: 560,
      html: `
        <div class="confirm-popup">
          <div class="confirm-header">
            <div class="confirm-icon ${isApprove ? "approve" : "reject"}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                ${isApprove ? '<path d="M5 13l4 4L19 7"/>' : '<path d="M6 18L18 6M6 6l12 12"/>'}
              </svg>
            </div>
            <p class="confirm-subtitle">
              You are about to ${isApprove ? "approve" : "reject"} ${actionedNets} of ${availableNets} available nets
            </p>
          </div>
          <div class="confirm-details">
            <div class="confirm-row"><span class="confirm-label">Farm</span><span class="confirm-value">${pool.farm_name || "—"}</span></div>
            <div class="confirm-row"><span class="confirm-label">GHSE</span><span class="confirm-value">${pool.ghse_name || "—"}</span></div>
            <div class="confirm-row"><span class="confirm-label">Variety</span><span class="confirm-value">${pool.variety_name || "—"}</span></div>
            <div class="confirm-row highlight">
              <span class="confirm-label">Nets Being Actioned</span>
              <span class="confirm-value big">${actionedNets}</span>
            </div>
            ${splitNote}
            <div class="confirm-divider"></div>
            <div class="confirm-checks-title">Inspection Results</div>
            ${checksHtml}
          </div>
        </div>`,
      showCancelButton: true,
      confirmButtonText: isApprove ? "Yes, Approve" : "Yes, Reject",
      cancelButtonText:  "Go Back",
      confirmButtonColor: isApprove ? "#a9ae50" : "#a92f6f",
      cancelButtonColor:  "#9e9a93",
      customClass: { popup: "qc-swal-popup confirm-swal" },
    });
    return r.isConfirmed;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // FORM HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!await showIntakeConfirmation()) return;
    try {
      await createIntake({
        pool:           form.pool,
        received_nets:  parseInt(form.received_nets  || 0),
        received_stems: parseInt(form.received_stems || 0),
      });
      await Swal.fire({ title: "Intake Recorded!", icon: "success", confirmButtonColor: "#a92f6f", timer: 2000, timerProgressBar: true });
      setForm({ pool: "", received_nets: "", received_stems: "" });
      loadData();
    } catch {
      Swal.fire("Error", "Failed to submit intake", "error");
    }
  };

  const validateQC = () => {
    if (!qcForm.intake)                                   return "Select intake";
    if (!qcForm.qc_nets || parseInt(qcForm.qc_nets) <= 0) return "Enter a valid number of QC nets";

    const sel = intakes.find((i) => String(i.id) === String(qcForm.intake));
    const rem = sel?.qc_progress?.remaining_qc ?? sel?.received_nets;
    if (sel && parseInt(qcForm.qc_nets) > rem) return `QC nets cannot exceed remaining (${rem})`;

    if (!qcForm.pest_disease_comment || !qcForm.cut_stage_comment || !qcForm.hooks_comment || !qcForm.arrangement_comment)
      return "All QC comments are required";
    if (qcForm.pest_disease_ok === null || qcForm.cut_stage_ok === null || qcForm.hooks_ok === null || qcForm.arrangement_ok === null)
      return "Please select Pass or Fail for all checks";
    return null;
  };

  const handleQC = async (e) => {
    e.preventDefault();
    const err = validateQC();
    if (err) { Swal.fire("Validation", err, "warning"); return; }
    if (!await showQCConfirmation()) return;
    try {
      await createQCCheck({ ...qcForm, qc_nets: parseInt(qcForm.qc_nets) });
      await Swal.fire({ title: "QC Submitted!", icon: "success", confirmButtonColor: "#a92f6f", timer: 2000, timerProgressBar: true });
      setQcForm({
        intake: "", qc_nets: "", status: "APPROVED",
        pest_disease_ok: null, pest_disease_comment: "",
        cut_stage_ok: null,    cut_stage_comment: "",
        hooks_ok: null,        hooks_comment: "",
        arrangement_ok: null,  arrangement_comment: "",
      });
      loadData();
    } catch {
      Swal.fire("Error", "QC failed", "error");
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // QUARANTINE REVIEW
  // ═══════════════════════════════════════════════════════════════════════════

  const handleReview = async (qc) => {
    const pool    = qc.intake_details?.pool || {};
    const { availableNets, availableStems, stemRatio } = deriveQuarantineBalance(qc);

    // Use available_nets as the cap — not the original qc_nets
    const actionCap = availableNets;

    const checksHtml = CHECK_KEYS.map(({ key, label }) => {
      const existingOk      = qc[`${key}_ok`];
      const existingComment = qc[`${key}_comment`] || "";
      return `
        <div style="margin-bottom:10px;background:#fff;padding:12px;border-radius:8px;border:1px solid #e8e4dc;">
          <label style="display:block;font-weight:600;color:#4a4036;margin-bottom:6px;">${label}</label>
          <select id="${key}_status" style="width:100%;padding:8px 12px;border-radius:6px;border:1px solid #d4cfc7;margin-bottom:8px;font-size:14px;">
            <option value="">Select Status</option>
            <option value="PASS" ${existingOk === true  ? "selected" : ""}>PASS</option>
            <option value="FAIL" ${existingOk === false ? "selected" : ""}>FAIL</option>
          </select>
          <textarea id="${key}_comment" placeholder="Enter comment..." style="width:100%;padding:8px 12px;border-radius:6px;border:1px solid #d4cfc7;font-size:14px;min-height:52px;resize:vertical;font-family:inherit;box-sizing:border-box;">${existingComment}</textarea>
        </div>`;
    }).join("");

    const collectAndValidate = () => {
      const data     = {};
      const netInput = document.getElementById("partial_nets");
      const partial  = parseInt(netInput?.value || 0);

      if (!partial || partial <= 0) {
        Swal.showValidationMessage("Enter at least 1 net to action");
        return false;
      }
      if (partial > actionCap) {
        Swal.showValidationMessage(`Cannot exceed ${actionCap} available nets`);
        return false;
      }
      data.partial_nets = partial;

      for (const { key } of CHECK_KEYS) {
        data[`${key}_status`]  = document.getElementById(`${key}_status`).value;
        data[`${key}_comment`] = document.getElementById(`${key}_comment`).value;
      }

      if (CHECK_KEYS.some(({ key }) => !data[`${key}_status`]))  { Swal.showValidationMessage("Select Pass or Fail for all checks"); return false; }
      if (CHECK_KEYS.some(({ key }) => !data[`${key}_comment`])) { Swal.showValidationMessage("All comments are required");          return false; }

      return data;
    };

    const result = await Swal.fire({
      title: "Review Quarantine Batch", width: 700,
      html: `
        <div style="text-align:left;font-family:inherit;">

          <!-- Context strip -->
          <div style="background:#faf9f6;padding:14px 16px;border-radius:10px;margin-bottom:12px;font-size:14px;">
            <p style="margin:3px 0;">
              <b style="color:#a92f6f;">Farm:</b> ${pool.farm_name || "—"} &nbsp;·&nbsp;
              <b style="color:#a92f6f;">GHSE:</b> ${pool.ghse_name || "—"} &nbsp;·&nbsp;
              <b style="color:#a92f6f;">Variety:</b> ${pool.variety_name || "—"}
            </p>
            <p style="margin:4px 0;color:#6b6356;font-size:13px;">
              Intake: <b>${qc.intake_details?.received_nets ?? "—"} nets</b> / <b>${qc.intake_details?.received_stems ?? "—"} stems</b>
            </p>
          </div>

          <!-- Quarantine balance ledger -->
          <div style="background:#fff8e6;border-left:3px solid #e6a817;border-radius:8px;padding:12px 16px;margin-bottom:12px;font-size:13px;color:#6b4c00;">
            <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;text-align:center;margin-bottom:8px;">
              <div>
                <div style="font-size:11px;text-transform:uppercase;letter-spacing:.05em;opacity:.7;margin-bottom:3px;">Original</div>
                <div style="font-size:18px;font-weight:700;">${qc.qc_nets}</div>
                <div style="font-size:11px;opacity:.7;">nets</div>
              </div>
              <div style="border-left:1px solid #e6c87a;border-right:1px solid #e6c87a;">
                <div style="font-size:11px;text-transform:uppercase;letter-spacing:.05em;opacity:.7;margin-bottom:3px;">Resolved</div>
                <div style="font-size:18px;font-weight:700;color:#a92f6f;">${qc.qc_nets - availableNets}</div>
                <div style="font-size:11px;opacity:.7;">nets</div>
              </div>
              <div>
                <div style="font-size:11px;text-transform:uppercase;letter-spacing:.05em;opacity:.7;margin-bottom:3px;">Available</div>
                <div style="font-size:18px;font-weight:700;color:#1a6b3c;">${availableNets}</div>
                <div style="font-size:11px;opacity:.7;">nets · ≈ ${availableStems} stems</div>
              </div>
            </div>
            <div style="height:6px;background:rgba(0,0,0,.1);border-radius:99px;overflow:hidden;">
              <div style="height:100%;width:${qc.qc_nets > 0 ? Math.round(((qc.qc_nets - availableNets) / qc.qc_nets) * 100) : 0}%;background:#a92f6f;border-radius:99px;"></div>
            </div>
            <div style="margin-top:5px;font-size:11px;opacity:.6;text-align:right;">
              ${qc.qc_nets > 0 ? Math.round(((qc.qc_nets - availableNets) / qc.qc_nets) * 100) : 0}% resolved
            </div>
          </div>

          <!-- Nets to action -->
          <div style="background:#fff;padding:12px;border-radius:8px;border:1px solid #e8e4dc;margin-bottom:14px;">
            <label style="display:block;font-weight:600;color:#4a4036;margin-bottom:6px;">
              Nets to Action Now &nbsp;<span style="font-weight:400;color:#9e9a93;font-size:12px;">(1 – ${actionCap})</span>
            </label>
            <div style="display:flex;align-items:center;gap:10px;">
              <input
                id="partial_nets"
                type="number" min="1" max="${actionCap}" value="${actionCap}"
                style="flex:1;padding:9px 12px;border-radius:6px;border:1px solid #d4cfc7;font-size:16px;font-weight:600;"
              />
              <span style="font-size:13px;color:#9e9a93;white-space:nowrap;">/ ${actionCap} available</span>
            </div>
            <p style="margin:7px 0 0;font-size:12px;color:#9e9a93;">
              Remaining in quarantine after this action:
              <b id="remaining_display" style="color:#e6a817;">0</b> nets
              · ≈ <b id="remaining_stems_display" style="color:#e6a817;">0</b> stems
            </p>
          </div>

          <!-- Inspection checks -->
          ${checksHtml}
        </div>`,

      didOpen: () => {
        const input        = document.getElementById("partial_nets");
        const netsDisp     = document.getElementById("remaining_display");
        const stemsDisp    = document.getElementById("remaining_stems_display");
        if (!input) return;

        const update = () => {
          const val          = Math.min(Math.max(parseInt(input.value) || 0, 0), actionCap);
          const leftNets     = actionCap - val;
          const leftStems    = Math.round(leftNets * stemRatio);
          netsDisp.textContent  = leftNets;
          stemsDisp.textContent = leftStems;
          const hasLeft = leftNets > 0;
          netsDisp.style.color  = hasLeft ? "#e6a817" : "#9e9a93";
          stemsDisp.style.color = hasLeft ? "#e6a817" : "#9e9a93";
        };

        update();
        input.addEventListener("input", update);
      },

      preConfirm: () => collectAndValidate(),
      preDeny:    () => collectAndValidate(),

      showCancelButton: true,
      showDenyButton:   true,
      confirmButtonText: "Approve Selected",
      denyButtonText:    "Reject Selected",
      cancelButtonText:  "Cancel",
      confirmButtonColor: "#a9ae50",
      denyButtonColor:    "#a92f6f",
      cancelButtonColor:  "#9e9a93",
      customClass: { popup: "qc-swal-popup" },
    });

    if (result.isDismissed) return;
    const formData = result.value;
    if (!formData) return;

    const action = result.isConfirmed ? "approve" : "reject";
    if (!await showReviewConfirmation(qc, action, formData)) return;

    try {
      if (result.isConfirmed) {
        await approveQCCheck(qc.id, formData);
      } else {
        await rejectQCCheck(qc.id, formData);
      }

      const actioned    = formData.partial_nets;
      const leftOver    = availableNets - actioned;
      const leftStems   = Math.round(leftOver * stemRatio);
      const actionWord  = result.isConfirmed ? "approved" : "rejected";

      await Swal.fire({
        title: result.isConfirmed ? "Approved!" : "Rejected",
        html: leftOver > 0
          ? `<b>${actioned} nets</b> ${actionWord}.<br/><b>${leftOver} nets</b> (≈ <b>${leftStems} stems</b>) remain in quarantine.`
          : `All <b>${actioned} nets</b> ${actionWord}.`,
        icon:               result.isConfirmed ? "success" : "error",
        confirmButtonColor: result.isConfirmed ? "#a9ae50" : "#a92f6f",
        timer: 3500, timerProgressBar: true,
      });
      loadData();
    } catch {
      Swal.fire("Error", "Action failed. Please try again.", "error");
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // FARM SELECTOR VIEW
  // ═══════════════════════════════════════════════════════════════════════════

  if (currentView === "selector") {
    return (
      <div className="farm-selector-page">
        <div className="farm-selector-content">
          <div className="selector-header">
            <div className="selector-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 21h18M5 21V7l8-4 8 4v14M8 21v-9a2 2 0 012-2h4a2 2 0 012 2v9" />
              </svg>
            </div>
            <h1>Select Farm</h1>
            <p>Choose a farm location to manage intake and quality control</p>
          </div>

          <div className="farm-cards-grid">
            {farmList.map((farm) => (
              <button key={farm} className="farm-card-btn" onClick={() => handleFarmSelect(farm)}>
                <div className="farm-card-visual">
                  <div className="farm-illustration">
                    <svg viewBox="0 0 64 64" fill="none">
                      <rect x="8" y="28" width="48" height="28" rx="2" fill="currentColor" opacity="0.15"/>
                      <path d="M4 28L32 8l28 20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <rect x="24" y="40" width="16" height="16" rx="1" fill="currentColor" opacity="0.3"/>
                      <circle cx="32" cy="20" r="4" fill="currentColor" opacity="0.4"/>
                    </svg>
                  </div>
                </div>
                <div className="farm-card-info">
                  <h3>{farm}</h3>
                  <span className="farm-status-badge">Active</span>
                </div>
                <div className="farm-card-arrow">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                </div>
              </button>
            ))}
          </div>

          <div className="selector-footer">
            <span className="brand-mark">IntakeQC System</span>
          </div>
        </div>
        <div className="selector-bg-pattern"></div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DASHBOARD VIEW
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="dashboard-page">
      <nav className="dashboard-nav">
        <div className="nav-brand">
          <div className="nav-logo">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 21h18M5 21V7l8-4 8 4v14"/>
            </svg>
          </div>
          <span className="nav-title">IntakeQC</span>
        </div>
        <div className="nav-farm-badge">
          <span className="farm-indicator"></span>
          {selectedFarm}
        </div>
        <button className="nav-back-btn" onClick={handleBackToSelector}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 19l-7-7 7-7"/></svg>
          Change Farm
        </button>
      </nav>

      <div className="dashboard-content">

        {/* ═══════════ INTAKE ═══════════ */}
        <section className="dash-section">
          <div className="section-header">
            <div className="section-icon intake-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 4v16m-8-8h16"/></svg>
            </div>
            <div><h2>New Intake</h2><p>Record incoming harvest from selected pool</p></div>
          </div>

          <div className="card intake-card">
            <form onSubmit={handleSubmit} className="intake-form">
              <div className="form-group">
                <label>Pool Selection</label>
                <select value={form.pool} onChange={(e) => setForm((p) => ({ ...p, pool: e.target.value }))}>
                  <option value="">Select a pool...</option>
                  {filteredPools.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.farm_name} | {p.ghse_name} | {p.variety_name} | Remaining: {p.remaining_nets} nets
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Received Nets</label>
                  <div className="input-with-suffix">
                    <input type="number" value={form.received_nets} onChange={(e) => handleNetChange(e.target.value)} placeholder="0" min="0"/>
                    <span className="input-suffix">nets</span>
                  </div>
                </div>
                <div className="form-group">
                  <label>Calculated Stems</label>
                  <div className="input-with-suffix readonly">
                    <input type="number" value={form.received_stems} readOnly placeholder="0"/>
                    <span className="input-suffix">stems</span>
                  </div>
                </div>
              </div>

              <button type="submit" className="btn-primary">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 13l4 4L19 7"/></svg>
                Record Intake
              </button>
            </form>
          </div>
        </section>

        {/* ═══════════ QC ═══════════ */}
        <section className="dash-section">
          <div className="section-header">
            <div className="section-icon qc-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            </div>
            <div><h2>Quality Control</h2><p>Perform inspection checks on today's pending intakes</p></div>
          </div>

          <div className="card qc-card">
            <form onSubmit={handleQC} className="qc-form">
              <div className="form-group">
                <label>Select Intake for QC</label>
                <select value={qcForm.intake} onChange={(e) => handleIntakeSelect(e.target.value)}>
                  <option value="">Choose intake...</option>
                  {filteredIntakes.map((i) => {
                    const pd   = i.pool_details || {};
                    const prog = i.qc_progress;
                    const label = prog
                      ? `${prog.remaining_qc} remaining (${prog.qc_done}/${prog.total_nets} done)`
                      : `${i.received_nets} nets`;
                    return (
                      <option key={i.id} value={i.id}>
                        {pd.farm_name} | {pd.ghse_name} | {pd.variety_name} | {label} | {fmtTime(i.received_at)}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="form-group">
                <label>QC Nets</label>
                <div className="input-with-suffix">
                  <input
                    type="number"
                    value={qcForm.qc_nets}
                    onChange={(e) => setQcForm((p) => ({ ...p, qc_nets: e.target.value }))}
                    placeholder="0" min="1"
                    max={(() => {
                      const i = intakes.find((x) => String(x.id) === String(qcForm.intake));
                      return i?.qc_progress?.remaining_qc ?? i?.received_nets ?? undefined;
                    })()}
                  />
                  <span className="input-suffix">nets</span>
                </div>
                {qcForm.intake && (() => {
                  const i    = intakes.find((x) => String(x.id) === String(qcForm.intake));
                  const prog = i?.qc_progress;
                  if (!i) return null;
                  return prog ? (
                    <span className="form-hint">{prog.remaining_qc} remaining · {prog.qc_done}/{prog.total_nets} done</span>
                  ) : (
                    <span className="form-hint">Intake received {i.received_nets} nets</span>
                  );
                })()}
              </div>

              <div className="form-group">
                <label>Overall Status</label>
                <div className="status-chips">
                  {["APPROVED", "QUARANTINE", "REJECTED"].map((s) => (
                    <button key={s} type="button" className={`status-chip ${qcForm.status === s ? "active" : ""}`} onClick={() => setQcForm((p) => ({ ...p, status: s }))}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="qc-checks-panel">
                <div className="qc-checks-header">
                  <span>Inspection Criteria</span>
                  <span className="qc-checks-sub">Toggle Pass/Fail for each check</span>
                </div>
                <QCRow label="Pest & Disease" status={qcForm.pest_disease_ok === true ? "PASS" : qcForm.pest_disease_ok === false ? "FAIL" : ""} setStatus={(v) => setQcForm((p) => ({ ...p, pest_disease_ok: v === "PASS" }))} commentKey="pest_disease" commentValue={qcForm.pest_disease_comment} onCommentChange={handleQcComment}/>
                <QCRow label="Cut Stage"      status={qcForm.cut_stage_ok === true    ? "PASS" : qcForm.cut_stage_ok === false    ? "FAIL" : ""} setStatus={(v) => setQcForm((p) => ({ ...p, cut_stage_ok:    v === "PASS" }))} commentKey="cut_stage"    commentValue={qcForm.cut_stage_comment}    onCommentChange={handleQcComment}/>
                <QCRow label="Hooks"          status={qcForm.hooks_ok === true        ? "PASS" : qcForm.hooks_ok === false        ? "FAIL" : ""} setStatus={(v) => setQcForm((p) => ({ ...p, hooks_ok:        v === "PASS" }))} commentKey="hooks"        commentValue={qcForm.hooks_comment}        onCommentChange={handleQcComment}/>
                <QCRow label="Arrangement"    status={qcForm.arrangement_ok === true  ? "PASS" : qcForm.arrangement_ok === false  ? "FAIL" : ""} setStatus={(v) => setQcForm((p) => ({ ...p, arrangement_ok:  v === "PASS" }))} commentKey="arrangement"  commentValue={qcForm.arrangement_comment}  onCommentChange={handleQcComment}/>
              </div>

              <button type="submit" className="btn-primary">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 13l4 4L19 7"/></svg>
                Submit QC Report
              </button>
            </form>
          </div>
        </section>

        {/* ═══════════ QUARANTINE ═══════════ */}
        <section className="dash-section quarantine-section">
          <div className="section-header">
            <div className="section-icon quarantine-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <div><h2>Quarantine Queue</h2><p>Review and resolve flagged quality checks</p></div>
            <span className="quarantine-count">{filteredQuarantine.length} pending</span>
          </div>

          {filteredQuarantine.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              </div>
              <h3>All Clear</h3>
              <p>No items in quarantine for {selectedFarm}</p>
            </div>
          ) : (
            <div className="quarantine-cards">
              {filteredQuarantine.map((q) => {
                const isExpanded = expandedQuarantine === q.id;
                const pool       = q.intake_details?.pool || {};
                const prog       = q.qc_progress || {};

                // Derive live balance figures from available_nets
                const { originalNets, availableNets, availableStems, resolvedNets } = deriveQuarantineBalance(q);
                const { approved, rejected, children: splitChildren } = tallySplitChildren(q.split_history);
                const resolvedPct = originalNets > 0 ? Math.round((resolvedNets / originalNets) * 100) : 0;

                return (
                  <div key={q.id} className={`quarantine-card ${isExpanded ? "expanded" : ""}`}>

                    {/* ── Card header (always visible) ── */}
                    <div className="quarantine-card-header" onClick={() => toggleQuarantineExpand(q.id)}>

                      <div className="quarantine-card-meta">
                        <div className="quarantine-farm-tag">{pool.farm_name || "—"}</div>
                        <div className="quarantine-time">{fmtDate(q.checked_at)} at {fmtTime(q.checked_at)}</div>
                        {q.has_split_children && (
                          <span style={{
                            display: "inline-block", marginLeft: "6px",
                            padding: "2px 7px", borderRadius: "99px",
                            fontSize: "11px", fontWeight: 600,
                            background: "#e6f4ea", color: "#1a6b3c",
                          }}>
                            Partially resolved
                          </span>
                        )}
                      </div>

                      <div className="quarantine-card-summary">
                        <div className="summary-item">
                          <span className="summary-label">GHSE</span>
                          <span className="summary-value">{pool.ghse_name || "—"}</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Variety</span>
                          <span className="summary-value">{pool.variety_name || "—"}</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Intake Nets</span>
                          <span className="summary-value highlight">{q.intake_details?.received_nets ?? "—"}</span>
                        </div>
                        {/* ── UPDATED: show available (remaining) not original qc_nets ── */}
                        <div className="summary-item">
                          <span className="summary-label">Still in Quarantine</span>
                          <span className="summary-value highlight" style={{ color: "#e6a817" }}>{availableNets}</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Quarantine Stems</span>
                          <span className="summary-value highlight" style={{ color: "#e6a817" }}>{availableStems}</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Intake Stems</span>
                          <span className="summary-value">{q.intake_details?.received_stems ?? "—"}</span>
                        </div>
                      </div>

                      <div className="quarantine-status-row">
                        <span className={`status-pill ${q.status.toLowerCase()}`}>{q.status}</span>
                        <button type="button" className="expand-btn" onClick={(e) => { e.stopPropagation(); toggleQuarantineExpand(q.id); }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                            style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}>
                            <path d="M19 9l-7 7-7-7"/>
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* ── Expanded body ── */}
                    {isExpanded && (
                      <div className="quarantine-card-body">

                        {/* ── Balance ledger bar ── */}
                        <div style={{ marginBottom: "14px", padding: "12px 14px", background: "var(--color-background-secondary)", borderRadius: "8px", border: "0.5px solid var(--color-border-tertiary)" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "8px" }}>
                            <span style={{ fontSize: "12px", fontWeight: 500, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Quarantine Balance
                            </span>
                            <span style={{ fontSize: "13px", color: "var(--color-text-secondary)" }}>
                              {availableNets}/{originalNets} nets remaining &nbsp;·&nbsp;
                              <span style={{ fontWeight: 500, color: resolvedNets > 0 ? "var(--color-text-success)" : "var(--color-text-secondary)" }}>
                                {resolvedNets} resolved
                              </span>
                            </span>
                          </div>
                          <div style={{ height: "6px", background: "var(--color-border-tertiary)", borderRadius: "99px", overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${resolvedPct}%`, background: resolvedPct === 100 ? "var(--color-text-success)" : "#a92f6f", borderRadius: "99px", transition: "width 0.3s ease" }}/>
                          </div>
                          <div style={{ marginTop: "5px", fontSize: "12px", color: "var(--color-text-tertiary)", textAlign: "right" }}>{resolvedPct}% resolved</div>

                          {/* Mini breakdown: approved vs rejected resolved */}
                          {(approved > 0 || rejected > 0) && (
                            <div style={{ display: "flex", gap: "12px", marginTop: "10px", fontSize: "12px" }}>
                              {approved > 0 && (
                                <span style={{ padding: "2px 8px", borderRadius: "99px", background: "#d4edda", color: "#1a6b3c", fontWeight: 600 }}>
                                  ✓ {approved} approved
                                </span>
                              )}
                              {rejected > 0 && (
                                <span style={{ padding: "2px 8px", borderRadius: "99px", background: "#fce8e8", color: "#922", fontWeight: 600 }}>
                                  ✗ {rejected} rejected
                                </span>
                              )}
                              <span style={{ color: "#e6a817", fontWeight: 600 }}>
                                ⏳ {availableNets} still pending
                              </span>
                            </div>
                          )}
                        </div>

                        {/* ── QC progress (from intake perspective) ── */}
                        {prog.total_nets > 0 && (() => {
                          const pct = Math.round(((prog.qc_done ?? 0) / prog.total_nets) * 100);
                          return (
                            <div style={{ marginBottom: "14px", padding: "12px 14px", background: "var(--color-background-secondary)", borderRadius: "8px", border: "0.5px solid var(--color-border-tertiary)" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "8px" }}>
                                <span style={{ fontSize: "12px", fontWeight: 500, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>QC Progress</span>
                                <span style={{ fontSize: "13px", color: "var(--color-text-secondary)" }}>
                                  {prog.qc_done}/{prog.total_nets} nets &nbsp;·&nbsp;
                                  <span style={{ fontWeight: 500, color: prog.remaining_to_qc > 0 ? "var(--color-text-warning)" : "var(--color-text-success)" }}>
                                    {prog.remaining_to_qc} uninspected
                                  </span>
                                </span>
                              </div>
                              <div style={{ height: "6px", background: "var(--color-border-tertiary)", borderRadius: "99px", overflow: "hidden" }}>
                                <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? "var(--color-text-success)" : "#a92f6f", borderRadius: "99px", transition: "width 0.3s ease" }}/>
                              </div>
                              <div style={{ marginTop: "5px", fontSize: "12px", color: "var(--color-text-tertiary)", textAlign: "right" }}>{pct}% inspected</div>
                            </div>
                          );
                        })()}

                        {/* ── Current quarantine hold notice ── */}
                        <div style={{ marginBottom: "14px", padding: "10px 14px", background: "#fff8e6", borderLeft: "3px solid #e6a817", borderRadius: "6px", fontSize: "13px", color: "#6b4c00" }}>
                          <strong>{availableNets} nets</strong> (≈ <strong>{availableStems} stems</strong>) currently held in this quarantine batch.
                          {q.has_split_children && <span> &nbsp;·&nbsp; <em>Some nets have already been partially resolved — see breakdown above.</em></span>}
                        </div>

                        {/* ── Split history ── */}
                        {splitChildren.length > 0 && (
                          <div style={{ marginBottom: "14px", padding: "12px 14px", background: "#f6faf6", border: "1px solid #c3e6c3", borderRadius: "8px" }}>
                            <p style={{ margin: "0 0 8px", fontSize: "12px", fontWeight: 600, color: "#1a6b3c", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              Resolution History
                            </p>
                            {splitChildren.map((child) => (
                              <div key={child.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: "1px solid #d4edda", fontSize: "13px", color: "#2d4a2d" }}>
                                <span style={{ fontWeight: 600 }}>{child.qc_nets} nets</span>
                                <span style={{
                                  padding: "2px 9px", borderRadius: "99px", fontSize: "11px", fontWeight: 600,
                                  background: child.status === "APPROVED" ? "#d4edda" : "#fce8e8",
                                  color:      child.status === "APPROVED" ? "#1a6b3c"  : "#922",
                                }}>
                                  {child.status}
                                </span>
                                <span style={{ color: "#6b6356" }}>{fmtDate(child.checked_at)} {fmtTime(child.checked_at)}</span>
                              </div>
                            ))}
                            {/* Remaining row */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", fontSize: "13px", color: "#6b4c00", fontWeight: 600 }}>
                              <span>{availableNets} nets</span>
                              <span style={{ padding: "2px 9px", borderRadius: "99px", fontSize: "11px", fontWeight: 600, background: "#fff8e6", color: "#6b4c00" }}>
                                PENDING
                              </span>
                              <span style={{ color: "#9e9a93" }}>still in quarantine</span>
                            </div>
                          </div>
                        )}

                        {/* ── Inspection criteria ── */}
                        <div className="checks-grid">
                          {CHECK_KEYS.map(({ key, label }) => (
                            <div key={key} className="check-detail">
                              <div className="check-detail-header">
                                <span className="check-name">{label}</span>
                                <span className={`check-badge ${q[`${key}_ok`] ? "pass" : "fail"}`}>
                                  {q[`${key}_ok`] ? "PASS" : "FAIL"}
                                </span>
                              </div>
                              <p className="check-comment">{q[`${key}_comment`] || "No comment provided"}</p>
                            </div>
                          ))}
                        </div>

                        <div className="quarantine-actions">
                          <button type="button" className="btn-review" onClick={() => handleReview(q)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                            </svg>
                            Review &amp; Update
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

      </div>
    </div>
  );
};

export default IntakeQC;